import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Payments as PaymentsIcon,
  Block as BlockIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as ActiveIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { adminApi } from '../../api';
import type { DashboardStats } from '../../types';
import type { RevenueStats } from '../../api/admin';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle } from '../../hooks/core';
import { isAdmin } from '../../types';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color?: string;
  onClick?: () => void;
}

function StatCard({ title, value, icon, color = 'primary.main', onClick }: StatCardProps) {
  return (
    <Card
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s',
        '&:hover': onClick ? { boxShadow: 6 } : {},
        height: '100%',
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: color,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h4" fontWeight="bold" noWrap>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useDocumentTitle('Admin Dashboard');

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin(user)) {
      navigate('/');
      return;
    }

    async function fetchStats() {
      try {
        setLoading(true);
        const [dashboardData, revenueData] = await Promise.all([
          adminApi.getDashboardStats(),
          adminApi.getRevenueStats(),
        ]);
        setStats(dashboardData);
        setRevenueStats(revenueData);
      } catch (err) {
        setError('Failed to load dashboard statistics');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, [user, navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Admin Dashboard
      </Typography>

      {/* User Statistics */}
      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
        Users
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total"
            value={stats.totalUsers}
            icon={<PeopleIcon />}
            color="#1976d2"
            onClick={() => navigate('/admin/users')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active"
            value={stats.activeUsers}
            icon={<ActiveIcon />}
            color="#2e7d32"
            onClick={() => navigate('/admin/users?status=active')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Disabled"
            value={stats.disabledUsers}
            icon={<BlockIcon />}
            color="#d32f2f"
            onClick={() => navigate('/admin/users?status=disabled')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Admins"
            value={stats.adminCount}
            icon={<AdminIcon />}
            color="#7b1fa2"
            onClick={() => navigate('/admin/users?role=admin')}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Board Statistics */}
      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
        Boards
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total"
            value={stats.totalBoards}
            icon={<DashboardIcon />}
            color="#0288d1"
            onClick={() => navigate('/admin/boards')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Draft"
            value={stats.totalBoards - stats.liveBoards - stats.completedBoards}
            icon={<DashboardIcon />}
            color="#f57c00"
            onClick={() => navigate('/admin/boards?status=draft')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Live"
            value={stats.liveBoards}
            icon={<DashboardIcon />}
            color="#388e3c"
            onClick={() => navigate('/admin/boards?status=live')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Completed"
            value={stats.completedBoards}
            icon={<DashboardIcon />}
            color="#616161"
            onClick={() => navigate('/admin/boards?status=completed')}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Board Manager Financials (NOT SquareUp Revenue) */}
      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
        Board Manager Activity
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Payments collected and payouts distributed by board managers. This is NOT SquareUp revenue.
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <StatCard
            title="Collected by Managers"
            value={formatCurrency(stats.totalRevenue)}
            icon={<PaymentsIcon />}
            color="#0288d1"
            onClick={() => navigate('/admin/payments')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <StatCard
            title="Paid Out to Winners"
            value={formatCurrency(stats.totalPayouts)}
            icon={<PaymentsIcon />}
            color="#7b1fa2"
            onClick={() => navigate('/admin/payments?entryType=payout')}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* SquareUp Platform Revenue */}
      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
        SquareUp Platform Revenue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Revenue from board credit purchases via Stripe.
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Revenue"
            value={formatCurrency((revenueStats?.totalRevenue || 0) / 100)}
            icon={<PaymentsIcon />}
            color="#2e7d32"
            onClick={() => navigate('/admin/transactions')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Transactions"
            value={revenueStats?.totalTransactions || 0}
            icon={<ReceiptIcon />}
            color="#7b1fa2"
            onClick={() => navigate('/admin/transactions')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Success Rate"
            value={revenueStats && revenueStats.totalTransactions > 0
              ? `${Math.round((revenueStats.successfulPayments / revenueStats.totalTransactions) * 100)}%`
              : '—'}
            icon={<ActiveIcon />}
            color="#616161"
            onClick={() => navigate('/admin/transactions')}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
